<template>
  <div class="relative" >
    <v-text-field
      name="search"
      label="Jump to..."
      v-model="query"
      :append-icon-cb="reset"
      @input="handleInput"
      @keyup.esc="reset"
      @keypress.down.prevent="handleDownArrow"
      @keypress.up.prevent="handleUpArrow"
      @keyup.enter="handleEnter"
      @keypress.tab="removeSelection"
      single-line
      prepend-icon="search"
      class="search-input"
      clearable
    />

    <v-card
      v-if="query"
      light
      class="absolute right-0 elevation-10"
      style="top: 45px; width: 500px; border: 1px solid #cfd3d6;"
      ref="results"
      @mouseover="removeSelection"
      >
        <div v-if="results.applicationNetworks.length" class="pb1" >
          <h1 class="title bg-light-gray p2 mb1">
            Application Networks
          </h1>
          <ResultLink
            v-for="item in results.applicationNetworks"
            :item="item"
            routeKey="application-networks"
            @click.native.exact="reset"
            :key="item.id"
          />
        </div>

        <div v-if="results.gateways.length" class="pb1">
          <h1 class="title bg-light-gray p2 mb1">
            Gateways
          </h1>
          <ResultLink
            v-for="item in results.gateways"
            :item="item"
            routeKey="gateways"
            @click.native.exact="reset"
            :key="item.id"
          />
        </div>

        <div v-if="results.endDevices.length" class="pb1">
          <h1 class="title bg-light-gray p2 mb1">
            End-devices
          </h1>
          <ResultLink
            v-for="item in results.endDevices"
            :item="item"
            routeKey="end-devices"
            @click.native.exact="reset"
            :key="item.id"
          />
        </div>

      <p v-if="noResults" class="italic px2 no-results">
        No results
      </p>
      <v-progress-linear :active="isLoading" :indeterminate="true" height="3" class="m0"/>
    </v-card>
  </div>
</template>


<script>
/* @flow */
import axios from 'axios'
import debounce from 'p-debounce'
import PQueue from 'p-queue'
import {NEW_NOTIFICATION} from '@/store/notifications'

const queue = new PQueue({concurrency: 1})

const ResultLink = {
  props: {
    item: Object,
    routeKey: String,
  },
  template: `
    <router-link
      :to="'/'+routeKey+'/' + item.eui"
      class="result-link block text-decoration-none black-text"
      tabindex="0"
      >
      <v-layout>
        <span style="flex-basis: 180px; flex-shrink: 0;">{{item.eui}}</span>
        <span class="medium">{{item.name}}</span>
      </v-layout>
    </router-link>
  `,
}

export default {
  name: 'Search',
  data() {
    return {
      query: '',
      results: {
        applicationNetworks: [],
        gateways: [],
        endDevices: [],
      },
      queue,
    }
  },
  computed: {
    isLoading() {
      return !!this.queue.pending
    },
    noResults() {
      const {results} = this
      return (
        !results.applicationNetworks.length &&
        !results.gateways.length &&
        !results.endDevices.length
      )
    },
  },
  methods: {
    reset() {
      window.document.activeElement.blur()
      this.query = ''
      this.results.applicationNetworks = []
      this.results.gateways = []
      this.results.endDevices = []
    },
    handleInput: debounce(function() {
      queue.add(this.fetchResults)
    }, 200),
    getSelectedResult() {
      return document.querySelector('.selected-result')
    },
    getResultLinks() {
      if (!this.$refs.results) return []
      return this.$refs.results.$el.getElementsByTagName('a')
    },
    selectFirstItem() {
      const links = this.getResultLinks()
      links[0] && links[0].classList.add('selected-result')
    },
    handleDownArrow() {
      const links = this.getResultLinks()
      const selected = this.getSelectedResult()
      // Default is first result
      let nextSelection = links[0]

      for (let i = 0; i < links.length; i++) {
        if (links[i] === selected) {
          if (i + 1 === links.length) {
            nextSelection = links[0]
          } else {
            nextSelection = links[i + 1]
          }
          break
        }
      }
      this.removeSelection()
      nextSelection && nextSelection.classList.add('selected-result')
    },
    handleUpArrow() {
      const links = this.getResultLinks()
      const selected = this.getSelectedResult()
      // Default is the last result
      let nextSelection = links[links.length - 1]

      for (let i = 0; i < links.length; i++) {
        if (links[i] === selected) {
          if (i === 0) {
            nextSelection = links[links.length - 1]
          } else {
            nextSelection = links[i - 1]
          }
          break
        }
      }
      this.removeSelection()
      nextSelection && nextSelection.classList.add('selected-result')
    },
    handleEnter() {
      const selected = this.getSelectedResult()
      selected && selected.click()
    },
    removeSelection() {
      const selected = this.getSelectedResult()
      selected && selected.classList.remove('selected-result')
    },
    async fetchResults() {
      try {
        const {
          data: {meta},
        } = await axios.get(`/search?q=${this.query}`)
        this.results.applicationNetworks = meta.application_networks || []
        this.results.gateways = meta.gateways || []
        this.results.endDevices = meta.end_devices || []
        // Wait until component is rerendered
        await this.$nextTick()
        this.selectFirstItem()
      } catch (error) {
        this.$store.dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load search results.',
          detail: error.message,
          color: 'error',
        })
      }
    },
  },
  components: {
    ResultLink,
  },
}
</script>

<style scoped>
p {
  margin: 0;
}

.no-results {
  padding: 4px 16px;
}
</style>

<style>
.search-input {
  min-width: 320px;
  margin-bottom: 3px;
}

.search-input .input-group__details {
  min-height: 0;
}

.search-input .input-group__prepend-icon {
  justify-content: center !important;
  margin-top: 3px;
}

.result-link {
  padding: 4px 16px;
}

.result-link.selected-result,
.result-link:hover,
.result-link:focus,
.result-link:active {
  background: #f8f9f9;
  outlined: 0;
}

@media only screen and (min-width: 1100px) {
  .search-input {
    min-width: 440px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Search.vue